import { P, type PermissionCode } from '../auth/permissions';
import type { OrderStatus } from './status';

/**
 * 订单状态机 —— 声明式转换表。
 *
 * 这是订单状态的**唯一真相**。任何直接写 order.status 的代码都是 bug。
 * 详见 docs/03-order-lifecycle.md §3.3。
 *
 * 前后端共用这张表:
 *   - 后端 OrderStateService.transition() 按它校验并执行
 *   - 前端按它算按钮显隐,不会出现「前端以为能点但后端拒绝」
 *
 * guards/effects 在这里只是**标识符**,具体实现在 apps/api 的 registry 里
 * (它们需要访问数据库,不能放进共享包)。
 */

export const ORDER_ACTIONS = [
  'CONFIRM', // 草稿确认为正式单(导入流程)
  'ASSIGN', // 调度员指派给司机
  'DISPATCH_TO_CHANNEL', // 交给三方承运方 —— 送的人不是我们的司机
  'GRAB', // 司机在订单池主动领取(V1 不接界面,转换表里留着)
  'ACCEPT', // 司机接单
  'REJECT', // 司机拒单
  'EXPIRE', // 接单超时,系统触发
  'UNASSIGN', // 调度员撤回派单
  'TRANSFER', // 转给其他司机
  'PICKUP', // 取货 —— 拍照门槛在这里
  'START_TRANSIT', // 开始配送(可选状态)
  'DELIVER', // 送达 —— 拍照门槛在这里
  'FAIL', // 投递失败
  'RETRY', // 失败后再次投递
  'RETURN', // 退回发货方
  'CANCEL', // 取消
] as const;

export type OrderAction = (typeof ORDER_ACTIONS)[number];

/** 谁触发的转换 */
export type ActorKind = 'USER' | 'DRIVER' | 'SYSTEM' | 'THIRD_PARTY';

export interface TransitionDef {
  /** 允许的起始状态 */
  from: readonly OrderStatus[];
  /** 目标状态 */
  to: OrderStatus;
  /** 允许触发的主体。DRIVER 还需通过 ownership guard */
  actors: readonly ActorKind[];
  /** 需要的权限点 */
  permission: PermissionCode;
  /** 前置条件标识符,实现见 apps/api/src/order/state/guards */
  guards: readonly string[];
  /** 同事务内的副作用 */
  effects: readonly string[];
  /** 事务外副作用,走 outbox 表由 worker 执行 */
  afterCommit: readonly string[];
  /** 司机端按钮文案的 i18n key。为空表示司机端不暴露此操作 */
  driverLabelKey?: string;
  /**
   * 目标状态受租户开关影响时的另一个去处。
   *
   * ★ 放在**转换表里**而不是藏在代码分支里 —— 「派单之后落到哪个状态」
   *   是这张表存在的全部意义,一旦它有两种可能,那两种都必须写在这儿。
   *   看表的人才能一眼答出「开了这个开关之后流程长什么样」。
   */
  toWhenFeature?: { feature: string; to: OrderStatus };
  /** 是否需要滑动确认(不可撤销的终态操作) */
  requiresSwipeConfirm?: boolean;
  /** 是否必填原因 */
  requiresReason?: boolean;
}

export const TRANSITIONS: Record<OrderAction, TransitionDef> = {
  CONFIRM: {
    from: ['DRAFT'],
    to: 'PENDING',
    actors: ['USER', 'SYSTEM'],
    permission: P.IMPORT_CONFIRM,
    guards: ['orderHasReceiver'],
    effects: ['setConfirmedAt'],
    afterCommit: ['enqueueGeocode', 'enqueueZoneMatch'],
  },

  ASSIGN: {
    from: ['PENDING', 'ASSIGNED'], // 已派单可直接改派
    to: 'ASSIGNED',
    actors: ['USER', 'SYSTEM'],
    permission: P.ORDER_ASSIGN,
    // ★ notHandedToChannel 排在最前:已经交给三方的单,
    //   连「这个司机在不在职」都不用查了 —— 根本不该派
    guards: ['notHandedToChannel', 'driverIsActive', 'driverBelowConcurrencyLimit'],
    /*
     * ★ 开了「派单免确认」之后,这一步直接落到 ACCEPTED,
     *   ASSIGNED 这个状态在正常流程里就不出现了。
     *
     *   为什么做成开关而不是直接改掉:两种都有人要。
     *   自有司机、当面派活的小店,让司机再点一次确认纯属多余;
     *   而众包/承包司机那边,「他接了没有」是必须留痕的一件事 ——
     *   派了没人接的单要能自动回到池子里(见接单超时那个 worker)。
     */
    toWhenFeature: { feature: 'assignWithoutAccept', to: 'ACCEPTED' },
    /*
     * ★ repriceDriverPayout 排在最后:它要读**这一步定下来的**司机
     *   (setAssignedAt 把 driverId 写进 patch),早一步跑就还是老司机。
     */
    effects: [
      'supersedePreviousAssignment',
      'createAssignment',
      'setAssignedAt',
      'setAcceptedAt',
      'repriceDriverPayout',
    ],
    afterCommit: ['pushNotifyDriver', 'scheduleAcceptTimeout'],
  },

  /**
   * 交给三方承运方。
   *
   * ★ 推给三方之后订单不能还留在 PENDING。留着的话:
   *   - 调度员看到的是「待派」,会给它再派一个自己的司机
   *   - 对方回调报「已接单」时,ACCEPT 的 from 不含 PENDING,会被状态机拒掉
   *     —— 于是整条回调链路一步都走不通
   *
   *   落到 ASSIGNED(待接单)语义上正好:已经交出去了,等承运方接。
   *   只是接的人不是我们的司机,所以不建 Assignment 记录。
   */
  DISPATCH_TO_CHANNEL: {
    from: ['PENDING'],
    to: 'ASSIGNED',
    actors: ['USER', 'SYSTEM'],
    permission: P.ORDER_ASSIGN,
    guards: [],
    effects: ['setChannelDispatch'],
    // 不推送司机、不排接单超时 —— 接单的是对方的系统,不是我们的人
    afterCommit: [],
  },

  GRAB: {
    from: ['PENDING'],
    to: 'ACCEPTED', // 一步到位,跳过 ASSIGNED
    actors: ['DRIVER'],
    permission: P.ORDER_GRAB,
    guards: [
      'policyAllowsGrab',
      'driverIsOnlineAndVerified',
      /*
       * ★ 排班只管「可领取」这一件事。
       *
       *   派给他的单照常派、照常接、照常送 —— ASSIGN / TRANSFER / ACCEPT
       *   上都**没有**这道守卫,这是刻意的(见 shift-gate.ts)。
       *
       *   这里拦一道是因为:池子里已经按排班滤过一遍了
       *   (pool.service 的 context),而池子的筛选和 GRAB 的守卫必须
       *   一一对应 —— 少这一条,一个知道单号的司机可以绕过列表直接领。
       */
      'driverHasShiftToday',
      'driverBelowConcurrencyLimit',
      'driverBelowDailyLimit',
      'orderInDriverServiceZone',
      'orderTierVisibleToDriver',
    ],
    effects: ['createAssignment', 'setAcceptedAt', 'freezePoolBonus', 'repriceDriverPayout'],
    afterCommit: ['notifyDispatcher', 'broadcastPoolRemoval'],
    driverLabelKey: 'order.action.grab',
  },

  ACCEPT: {
    from: ['ASSIGNED'],
    to: 'ACCEPTED',
    /*
     * ★ THIRD_PARTY:推给三方平台的单,由对方的回调来推进状态。
     *   不加的话回调只能记事件不能改状态,订单会永远停在 ASSIGNED ——
     *   而调度员看到的是「一直没人接」,会去催一个根本不存在的司机。
     *
     *   越权风险由 webhook 处理器兜住:一条通道只能动
     *   `channel_id` 是自己的单(见 webhook.service.ts)。
     */
    /*
     * ★ USER:后台**代司机接一下**。
     *
     *   用户报的:「订单是待接单状态的时候,管理端批量操作只能转派或者取消,
     *   不能继续下一步标记已经取货等」。
     *
     *   在这之前,一张派下去而司机没点确认的单,后台只有三条路:
     *   转派、撤回、取消 —— 而现实里货常常**已经拉走了**(当面派的活、
     *   司机手机没电、app 没装上)。取消重下会换单号、废掉司机跑过的那一趟;
     *   放着不动则是这单后面的取货、送达、结算全部卡住。
     *
     *   取货(PICKUP)和送达(DELIVER)早就允许后台代记了,
     *   唯独中间这一步没有 —— 于是那两个入口对「待接单」的单毫无用处。
     */
    actors: ['DRIVER', 'USER', 'THIRD_PARTY'],
    permission: P.ORDER_ACCEPT,
    guards: ['isAssignedDriver', 'assignmentNotExpired', 'staffNotAcceptingChannelOrder'],
    effects: ['markAssignmentAccepted', 'setAcceptedAt'],
    afterCommit: ['cancelAcceptTimeout', 'notifyDispatcher'],
    driverLabelKey: 'order.action.accept',
  },

  REJECT: {
    from: ['ASSIGNED'],
    to: 'PENDING',
    actors: ['DRIVER'],
    permission: P.ORDER_ACCEPT,
    guards: ['isAssignedDriver'],
    effects: ['markAssignmentRejected', 'clearDriver'],
    afterCommit: ['notifyDispatcher'],
    driverLabelKey: 'order.action.reject',
    requiresReason: true,
  },

  EXPIRE: {
    from: ['ASSIGNED'],
    to: 'PENDING',
    actors: ['SYSTEM'],
    permission: P.ORDER_ASSIGN,
    guards: ['assignmentIsExpired'],
    /*
     * ★ 这里原来还挂着一个 `incrDriverTimeoutCount`,而它在 effects.ts 里
     *   是个空函数(明写着「P1 再做」)—— 转换表宣称的效果什么都不做。
     *
     *   已经去掉。「这个司机最近超时几次」不需要另存一个计数器:
     *   `markAssignmentExpired` 会把那条派单记成 EXPIRED,
     *   而 assignment 上带着 driverId 和时间 —— 数它就行,
     *   而且不会像冗余计数器那样漂。
     */
    effects: ['markAssignmentExpired', 'clearDriver'],
    afterCommit: ['notifyDispatcher'],
  },

  UNASSIGN: {
    from: ['ASSIGNED', 'ACCEPTED'],
    to: 'PENDING',
    actors: ['USER'],
    permission: P.ORDER_UNASSIGN,
    guards: ['orderNotPickedUp'],
    effects: ['revokeAssignment', 'clearDriver'],
    afterCommit: ['pushNotifyDriver'],
    requiresReason: true,
  },

  TRANSFER: {
    from: ['ASSIGNED', 'ACCEPTED', 'PICKED_UP', 'IN_TRANSIT'],
    to: 'ASSIGNED', // 转给新司机后回到待接单
    actors: ['USER'],
    permission: P.ORDER_TRANSFER,
    guards: ['notHandedToChannel', 'targetDriverIsActive', 'targetDriverBelowLimit'],
    /*
     * ★ 改派要重算酬劳 —— 换了个人,他谈的价可能不一样。
     *   已送达的单不受影响(那时 price_frozen_at 已经落下),
     *   而送达之前改派,这一趟本来就是新司机跑完的。
     */
    effects: [
      'supersedePreviousAssignment',
      'createAssignment',
      'resetAcceptedAt',
      'repriceDriverPayout',
    ],
    afterCommit: ['pushNotifyBothDrivers', 'scheduleAcceptTimeout'],
    requiresReason: true,
  },

  PICKUP: {
    from: ['ACCEPTED'],
    to: 'PICKED_UP',
    /*
     * ★ THIRD_PARTY:推给三方平台的单,由对方的回调来推进状态。
     *   不加的话回调只能记事件不能改状态,订单会永远停在 ASSIGNED ——
     *   而调度员看到的是「一直没人接」,会去催一个根本不存在的司机。
     *
     *   越权风险由 webhook 处理器兜住:一条通道只能动
     *   `channel_id` 是自己的单(见 webhook.service.ts)。
     *
     * ★ USER:后台代记一次取货。送达(DELIVER)一直都允许后台代记,
     *   取货却不允许 —— 于是司机手机没电、忘了点、app 装不上的时候,
     *   这一单卡死在「待取货」:后台推不动,而它已经在车上跑了。
     *   卡住的不只是这张单的状态,还有它后面的送达、结算和对账。
     *
     *   代记的人不在现场,所以围栏对他不生效(见 pickupGeofenceSatisfied);
     *   但**拍照门槛照旧** —— 那道门是商家要的凭证,
     *   给后台开个后门等于所有需要拍照的单都能绕过去。
     */
    actors: ['DRIVER', 'USER', 'THIRD_PARTY'],
    permission: P.ORDER_PICKUP,
    // ★ pickupProofIfRequired 是门槛:没拍照就转换失败,订单留在 ACCEPTED
    guards: ['isAssignedDriverOrDispatcher', 'pickupGeofenceSatisfied', 'pickupProofIfRequired'],
    effects: ['attachPickupProofs', 'setPickedUpAt', 'recordGeoStamp'],
    /*
     * ★ 取货时也通知**收件人**一次。
     *   在这之前只有「已送达」那一条 —— 而收件人最想知道的是
     *   「货已经在路上了」:他要据此安排时间在家。
     *   等收到「已送达」时,这条信息已经没有用了。
     */
    afterCommit: ['notifyCustomer', 'notifyReceiver'],
    driverLabelKey: 'order.action.pickup',
  },

  START_TRANSIT: {
    from: ['PICKED_UP'],
    to: 'IN_TRANSIT',
    /*
     * ★ THIRD_PARTY:推给三方平台的单,由对方的回调来推进状态。
     *   不加的话回调只能记事件不能改状态,订单会永远停在 ASSIGNED ——
     *   而调度员看到的是「一直没人接」,会去催一个根本不存在的司机。
     *
     *   越权风险由 webhook 处理器兜住:一条通道只能动
     *   `channel_id` 是自己的单(见 webhook.service.ts)。
     */
    actors: ['DRIVER', 'THIRD_PARTY'],
    permission: P.ORDER_DELIVER,
    guards: ['isAssignedDriver', 'inTransitEnabled'],
    effects: ['setTransitStartedAt', 'recordGeoStamp'],
    afterCommit: [],
    driverLabelKey: 'order.action.startTransit',
  },

  DELIVER: {
    from: ['PICKED_UP', 'IN_TRANSIT'],
    to: 'DELIVERED',
    /*
     * ★ THIRD_PARTY:推给三方平台的单,由对方的回调来推进状态。
     *   不加的话回调只能记事件不能改状态,订单会永远停在 ASSIGNED ——
     *   而调度员看到的是「一直没人接」,会去催一个根本不存在的司机。
     *
     *   越权风险由 webhook 处理器兜住:一条通道只能动
     *   `channel_id` 是自己的单(见 webhook.service.ts)。
     */
    actors: ['DRIVER', 'USER', 'THIRD_PARTY'],
    permission: P.ORDER_DELIVER,
    // ★ deliveryProofIfRequired 同样是门槛:没拍照就留在 PICKED_UP
    guards: [
      'isAssignedDriverOrDispatcher',
      'deliveryProofIfRequired',
      'codCollectedIfNeeded',
      // ★ 车上有冻品/易碎这类东西时,要逐项确认真的交出去了
      'specialItemsHandedOver',
    ],
    effects: ['attachDeliveryProofs', 'setDeliveredAt', 'recordGeoStamp', 'freezePricing'],
    afterCommit: ['notifyCustomer', 'notifyReceiver', 'pushToChannelIfConfigured'],
    driverLabelKey: 'order.action.deliver',
    requiresSwipeConfirm: true,
  },

  FAIL: {
    from: ['ACCEPTED', 'PICKED_UP', 'IN_TRANSIT'],
    to: 'FAILED',
    /*
     * ★ THIRD_PARTY:推给三方平台的单,由对方的回调来推进状态。
     *   不加的话回调只能记事件不能改状态,订单会永远停在 ASSIGNED ——
     *   而调度员看到的是「一直没人接」,会去催一个根本不存在的司机。
     *
     *   越权风险由 webhook 处理器兜住:一条通道只能动
     *   `channel_id` 是自己的单(见 webhook.service.ts)。
     */
    actors: ['DRIVER', 'USER', 'THIRD_PARTY'],
    permission: P.ORDER_FAIL,
    guards: ['isAssignedDriverOrDispatcher', 'failReasonProvided', 'failDispositionProvided'],
    effects: ['incrFailedAttempts', 'setSubStatus', 'recordGeoStamp', 'attachFailProofs'],
    afterCommit: ['notifyDispatcher', 'maybeAutoReturn'],
    driverLabelKey: 'order.action.fail',
    requiresReason: true,
  },

  RETRY: {
    from: ['FAILED'],
    /**
     * ★ 回到 PICKED_UP,不是 ACCEPTED。
     *
     * ACCEPTED 在本系统里是「待取货」。投递失败时货还在司机车上,
     * 转回 ACCEPTED 意味着这单又出现在取货清单里,
     * 系统会要求司机回取货点**再取一次并拍照** —— 而货根本没离开过他。
     * 失败重投的语义是「货在手上,再跑一趟收货地」,所以是 PICKED_UP。
     */
    to: 'PICKED_UP',
    actors: ['DRIVER', 'USER'],
    permission: P.ORDER_DELIVER,
    guards: ['isAssignedDriverOrDispatcher', 'belowMaxAttempts'],
    effects: ['clearSubStatus'],
    afterCommit: [],
    driverLabelKey: 'order.action.retry',
  },

  RETURN: {
    from: ['FAILED', 'PICKED_UP', 'IN_TRANSIT'],
    to: 'RETURNED',
    actors: ['DRIVER', 'USER'],
    permission: P.ORDER_FAIL,
    guards: ['isAssignedDriverOrDispatcher'],
    effects: ['setReturnedAt', 'recordGeoStamp', 'attachReturnProofs'],
    afterCommit: ['notifyCustomer', 'notifyDispatcher'],
    driverLabelKey: 'order.action.return',
    requiresReason: true,
  },

  CANCEL: {
    from: ['DRAFT', 'PENDING', 'ASSIGNED', 'ACCEPTED', 'PICKED_UP', 'IN_TRANSIT', 'FAILED'],
    to: 'CANCELLED',
    /*
     * ★ THIRD_PARTY:推给三方平台的单,由对方的回调来推进状态。
     *   不加的话回调只能记事件不能改状态,订单会永远停在 ASSIGNED ——
     *   而调度员看到的是「一直没人接」,会去催一个根本不存在的司机。
     *
     *   越权风险由 webhook 处理器兜住:一条通道只能动
     *   `channel_id` 是自己的单(见 webhook.service.ts)。
     */
    actors: ['USER', 'THIRD_PARTY'],
    permission: P.ORDER_CANCEL,
    // 已取货时必须交代货物去向,否则会出现「货去哪了」的纠纷
    guards: ['notAlreadySettled', 'goodsDispositionIfPickedUp'],
    effects: ['setCancelledAt', 'revokeAssignments', 'releaseCharges'],
    afterCommit: ['pushNotifyDriver', 'notifyCustomer', 'cancelOnChannelIfPushed'],
    requiresReason: true,
  },
};

/** 从某状态出发的全部合法动作 */
export function allowedActions(status: OrderStatus): OrderAction[] {
  return (Object.keys(TRANSITIONS) as OrderAction[]).filter((a) =>
    TRANSITIONS[a].from.includes(status),
  );
}

/** 某个主体在某状态下能做的动作(不含权限和 guard 校验,那是后端的事) */
export function allowedActionsFor(status: OrderStatus, actor: ActorKind): OrderAction[] {
  return allowedActions(status).filter((a) => TRANSITIONS[a].actors.includes(actor));
}

/** 司机端在某状态下应该显示的操作按钮 */
export function driverActionsFor(status: OrderStatus): OrderAction[] {
  return allowedActionsFor(status, 'DRIVER').filter((a) => TRANSITIONS[a].driverLabelKey);
}

export function canTransition(from: OrderStatus, action: OrderAction): boolean {
  return TRANSITIONS[action].from.includes(from);
}
